import { ICategory, IProduct } from 'store/api'
import { IProductDetail as ProductDetailFields} from 'components/product/ProductCard/types'
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  MEDIA_URL,
  TELEGRAM_LINK,
  VK_LINK,
} from 'config/constants'
import { stripHtml } from './htmlText'

// API отдаёт пути картинок от корня (/upload/...) — для schema.org нужен абсолютный адрес медиа-хоста.
const absoluteImageUrl = (u: string): string =>
  /^https?:\/\//.test(u) ? u : `${MEDIA_URL}${u}`

const SITE_URL = 'https://www.moscowfood.ru'
const RESTAURANT_ID = `${SITE_URL}/#restaurant`

/* ------------------------------------------------------------------ */
/*  helpers                                                           */
/* ------------------------------------------------------------------ */

const toPositiveNumber = (
  v: string | number | null | undefined
): number => {
  if (v === null || v === undefined || v === '') return 0
  const n = typeof v === 'string' ? parseFloat(v) : v
  return Number.isFinite(n) && n > 0 ? n : 0
}

const dedupe = (arr: string[]): string[] =>
  Array.from(new Set(arr.filter(Boolean)))

/* ------------------------------------------------------------------ */
/*  Restaurant (root entity)                                          */
/* ------------------------------------------------------------------ */

// Один адрес на Restaurant и Organization — чтобы не разъехались.
const POSTAL_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: 'Скаковая улица, 36',
  addressLocality: 'Москва',
  postalCode: '125040',
  addressCountry: 'RU',
}

export const restaurantNode = (opts?: { hasMenuId?: string }) => {
  const node: Record<string, unknown> = {
    '@type': ['Restaurant', 'FoodEstablishment'],
    '@id': RESTAURANT_ID,
    name: 'MOSCOW FOOD',
    alternateName: 'MOSCOW FOOD Кейтеринг',
    url: `${SITE_URL}/`,
    additionalType: 'https://schema.org/CateringService',
    description:
      'Премиальный кейтеринг в Москве. Корпоративные обеды, фуршеты, банкеты, выездное обслуживание.',
    servesCuisine: ['European', 'Russian', 'Asian', 'Japanese'],
    priceRange: '₽₽₽',
    address: POSTAL_ADDRESS,
    // TODO: после согласования с менеджментом добавить:
    //   - geo: { '@type': 'GeoCoordinates', latitude, longitude }
    //   - telephone: '+7-...'
    //   - openingHoursSpecification: [...]
    //   - sameAs: [<vk>, <telegram>, ...]
  }

  if (opts?.hasMenuId) {
    node['hasMenu'] = { '@id': opts.hasMenuId }
  }

  return node
}

/* ------------------------------------------------------------------ */
/*  Organization (главная страница)                                   */
/* ------------------------------------------------------------------ */

// Только данные, которые уже есть на сайте: имя и адрес — как у
// Restaurant, телефон/почта/соцсети — config/constants (их показывает
// подвал), логотип — фирменная звезда из public/ (иконка 512×512 из
// _document.tsx и site.webmanifest; растровой версии логотипа-надписи
// в проекте нет — шапка рисует его inline-SVG).
export const ORGANIZATION_LOGO_PATH = '/android-chrome-512x512.png'

export const buildOrganizationGraph = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'MOSCOW FOOD',
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}${ORGANIZATION_LOGO_PATH}`,
  telephone: CONTACT_PHONE,
  email: CONTACT_EMAIL,
  address: POSTAL_ADDRESS,
  sameAs: [VK_LINK, TELEGRAM_LINK],
})

/* ------------------------------------------------------------------ */
/*  MenuItem                                                          */
/* ------------------------------------------------------------------ */

type MenuItemArgs = {
  product: IProduct
  category: ICategory // используется только для построения URL
  detail?: ProductDetailFields
}

export const menuItemNode = ({
  product,
  category,
  detail,
}: MenuItemArgs) => {
  const url = `${SITE_URL}/product/${category.slug}/${product.slug}/`
  const itemId = `${url}#menuitem`

  const description = stripHtml(
    detail?.product?.full_desc ||
      product.short_desc ||
      product.meta?.product_description
  )

  // Изображения — приоритет: fullImage → product.image → additional_images
  const images: string[] = []
  //if (detail?.fullImage) images.push(detail.fullImage)
  if (detail?.product?.image) images.push(detail.product?.image)
  if (product.image) images.push(product.image)

  const additionalImages = detail?.product?.additional_images

  if (additionalImages?.length) {
    for (const img of additionalImages) {
      //const u =
      //  typeof img === 'string' ? img : img.url || img.src || ''
      //if (u) images.push(u)
      if (img) images.push(img)
    }
  }
  const uniqueImages = dedupe(images).map(absoluteImageUrl)

  const node: Record<string, unknown> = {
    '@type': 'MenuItem',
    '@id': itemId,
    name: product.name,
    url,
  }
  if (description) node['description'] = description
  if (uniqueImages.length === 1) node['image'] = uniqueImages[0]
  else if (uniqueImages.length > 1) node['image'] = uniqueImages

  // Offer (только если цена > 0)
  if (product.price > 0) {
    node['offers'] = {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'RUB',
      availability: 'https://schema.org/InStock',
      url,
    }
  }

  // NutritionInformation (только если есть хоть одно ненулевое значение)
  if (detail?.product) {
    const tm = detail.product
    const kcal = toPositiveNumber(tm.kcal)
    const proteins = toPositiveNumber(tm.proteins)
    const fats = toPositiveNumber(tm.fats)
    const carbs = toPositiveNumber(tm.hydrocarbonates)
    const weight = tm.weight.toString()

    if (kcal || proteins || fats || carbs) {
      const nutrition: Record<string, unknown> = {
        '@type': 'NutritionInformation',
      }
      if (kcal) nutrition['calories'] = `${kcal} kcal`
      if (proteins) nutrition['proteinContent'] = `${proteins} g`
      if (fats) nutrition['fatContent'] = `${fats} g`
      if (carbs) nutrition['carbohydrateContent'] = `${carbs} g`
      if (weight) nutrition['servingSize'] = weight
      node['nutrition'] = nutrition
    }
  }

  return node
}

/* ------------------------------------------------------------------ */
/*  MenuSection                                                       */
/* ------------------------------------------------------------------ */

// Каноническая категория товара (lib/productCanonical.ts): по ней строится
// url/@id MenuItem, чтобы у товара из нескольких категорий разметка
// совпадала с <link rel=canonical> страницы товара. Нет — категория
// текущего URL, как было.
type CanonicalCategoryOf = (product: IProduct) => ICategory | undefined

type MenuSectionArgs = {
  category: ICategory
  products: IProduct[]
  parentCategory?: ICategory // если category — это подкатегория
  canonicalCategoryOf?: CanonicalCategoryOf
}

export const menuSectionNode = ({
  category,
  products,
  parentCategory,
  canonicalCategoryOf,
}: MenuSectionArgs) => {
  const slugPath = parentCategory
    ? `${parentCategory.slug}/${category.slug}`
    : category.slug
  const sectionUrl = `${SITE_URL}/categories/${slugPath}/`
  const description = stripHtml(category.text)

  // Для построения URL продукта нужна категория верхнего уровня
  const categoryForProductUrl = parentCategory ?? category

  const node: Record<string, unknown> = {
    '@type': 'MenuSection',
    '@id': `${sectionUrl}#menusection`,
    name: category.name,
    url: sectionUrl,
    hasMenuItem: products.map((p) =>
      menuItemNode({
        product: p,
        category: canonicalCategoryOf?.(p) ?? categoryForProductUrl,
      })
    ),
  }
  if (description) node['description'] = description
  if (category.image) node['image'] = category.image

  return node
}

/* ------------------------------------------------------------------ */
/*  Граф для страницы категории                                       */
/* ------------------------------------------------------------------ */

export const buildCategoryGraph = (args: {
  category: ICategory
  parentCategory?: ICategory
  products: IProduct[]
  canonicalCategoryOf?: CanonicalCategoryOf
}) => {
  const slugPath = args.parentCategory
    ? `${args.parentCategory.slug}/${args.category.slug}`
    : args.category.slug
  const menuId = `${SITE_URL}/categories/${slugPath}/#menu`

  // Вложенность для подкатегории: внешняя секция родителя содержит
  // hasMenuSection с текущей подкатегорией как вложенной секцией.
  let topSection
  if (args.parentCategory) {
    const innerSection = menuSectionNode({
      category: args.category,
      products: args.products,
      parentCategory: args.parentCategory,
      canonicalCategoryOf: args.canonicalCategoryOf,
    })
    topSection = {
      '@type': 'MenuSection',
      '@id': `${SITE_URL}/categories/${args.parentCategory.slug}/#menusection`,
      name: args.parentCategory.name,
      url: `${SITE_URL}/categories/${args.parentCategory.slug}/`,
      hasMenuSection: [innerSection],
    }
  } else {
    topSection = menuSectionNode({
      category: args.category,
      products: args.products,
      canonicalCategoryOf: args.canonicalCategoryOf,
    })
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      restaurantNode({ hasMenuId: menuId }),
      {
        '@type': 'Menu',
        '@id': menuId,
        name: args.category.name,
        inLanguage: 'ru-RU',
        hasMenuSection: [topSection],
      },
    ],
  }
}

/* ------------------------------------------------------------------ */
/*  Граф для страницы товара                                          */
/* ------------------------------------------------------------------ */

export const buildProductGraph = (args: {
  product: IProduct
  category: ICategory
  // url/@id MenuItem строятся от неё (= canonical страницы); секция и
  // меню остаются категорией текущего URL.
  canonicalCategory?: ICategory
  detail?: ProductDetailFields
}) => {
  const sectionUrl = `${SITE_URL}/categories/${args.category.slug}/`
  const sectionId = `${sectionUrl}#menusection`
  const menuId = `${sectionUrl}#menu`

  return {
    '@context': 'https://schema.org',
    '@graph': [
      restaurantNode({ hasMenuId: menuId }),
      {
        '@type': 'Menu',
        '@id': menuId,
        name: args.category.name,
        inLanguage: 'ru-RU',
        hasMenuSection: [
          {
            '@type': 'MenuSection',
            '@id': sectionId,
            name: args.category.name,
            url: sectionUrl,
            hasMenuItem: [
              menuItemNode({
                product: args.product,
                category: args.canonicalCategory ?? args.category,
                detail: args.detail,
              }),
            ],
          },
        ],
      },
    ],
  }
}
